import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { type PaginatorState } from 'primeng/paginator';

import { AuthService } from '@/core/auth/auth.service';
import { PainelLateral } from '@/shared/components/painel-lateral/painel-lateral';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { MapaUfCard } from '@/shared/components/mapa-uf-card/mapa-uf-card';
import { CardContent } from '@/shared/components/card-content/card-content';
import { MedicoControleAdministrativoFeatureService } from '../mapa-brasil/medico-controle-administrativo.service';
import { MedicoEstatisticaAgrupadaResponse } from '@/core/api/sicom-api/registro/medico/controle-administrativo/medico-estatistica-agrupada.model';
import { MedicoSituacaoEnderecoTipoResponse } from '@/core/api/sicom-api/registro/medico/controle-administrativo/medico-situacao-endereco-tipo.model';
import { MedicoPendenciaResponse } from '@/core/api/sicom-api/registro/medico/pendencia/medico-pendencia.model';
import { DetalhamentoPendencias } from '../detalhamento-pendencias/detalhamento-pendencias';
import { IndicadorPendencia } from '../mapa-pendencias/indicador-pendencia.model';
import { PendenciaLinha } from '../mapa-pendencias/pendencia-linha.model';
import { UfSelecaoGestaoService } from '../uf-selecao-gestao.service';

type OpcaoMes = { label: string; value: number };
type DrillCard = 'situacao' | 'movimento';
type LinhaBloco = { descricao: string; total: number; share: number; barra: number; dominante: boolean; codigo: string | null };

@Component({
    selector: 'app-situacao-mensal',
    standalone: true,
    imports: [CommonModule, FormsModule, InputTextModule, CardModule, TableModule, SelectModule, ButtonModule, SkeletonModule, PainelLateral, SelectEstado, MapaUfCard, CardContent, DetalhamentoPendencias],
    templateUrl: './situacao-mensal.html',
    styleUrl: './situacao-mensal.scss'
})
export class SituacaoMensal implements OnInit {
    readonly anoAtual = new Date().getFullYear();
    readonly meses: OpcaoMes[] = [
        { label: 'Janeiro', value: 1 },
        { label: 'Fevereiro', value: 2 },
        { label: 'Março', value: 3 },
        { label: 'Abril', value: 4 },
        { label: 'Maio', value: 5 },
        { label: 'Junho', value: 6 },
        { label: 'Julho', value: 7 },
        { label: 'Agosto', value: 8 },
        { label: 'Setembro', value: 9 },
        { label: 'Outubro', value: 10 },
        { label: 'Novembro', value: 11 },
        { label: 'Dezembro', value: 12 }
    ];

    ufSelecionada: string | null = null;
    ufsPermitidas: string[] | null = null;
    mesSelecionado = new Date().getMonth() + 1;
    anoSelecionado = this.anoAtual;

    // Snapshot dos filtros efetivamente aplicados (para o cabeçalho/KPIs não
    // mudarem enquanto o usuário edita o mês/ano sem clicar em Aplicar).
    mesAplicado = new Date().getMonth() + 1;
    anoAplicado = this.anoAtual;
    ufAplicada: string | null = null;

    situacaoItens: MedicoEstatisticaAgrupadaResponse[] = [];
    sexoItens: MedicoEstatisticaAgrupadaResponse[] = [];
    movimentoItens: MedicoEstatisticaAgrupadaResponse[] = [];
    cancelamentosItens: MedicoEstatisticaAgrupadaResponse[] = [];
    enderecoTipoItens: MedicoSituacaoEnderecoTipoResponse[] = [];

    situacaoLinhas: LinhaBloco[] = [];
    sexoLinhas: LinhaBloco[] = [];
    movimentoLinhas: LinhaBloco[] = [];
    cancelamentosLinhas: LinhaBloco[] = [];

    carregando = false;

    detalheAtivo = false;
    detalheCard: DrillCard | null = null;
    detalheCodigo: string | null = null;
    detalheRotulo = '';
    linhasDetalhe: PendenciaLinha[] = [];
    totalDetalhe = 0;
    firstDetalhe = 0;
    rowsDetalhe = 100;
    carregandoDetalhe = false;

    private ufTokenRestrita: string | null = null;
    private pendentes = 0;
    private readonly aplicarChange$ = new Subject<void>();
    private readonly detalheChange$ = new Subject<void>();

    constructor(
        private feature: MedicoControleAdministrativoFeatureService,
        private authService: AuthService,
        private destroyRef: DestroyRef,
        private ufSelecaoGestao: UfSelecaoGestaoService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.configurarUfPorToken();
        this.aplicar();
    }

    onUfSelecionadaChange(uf?: string | null): void {
        this.ufSelecionada = this.normalizarUfSelecionada(uf);
        this.ufSelecaoGestao.definir(this.ufSelecionada);
    }

    aplicar(): void {
        this.voltarParaCards();
        this.mesSelecionado = this.normalizarMes(this.mesSelecionado);
        this.anoSelecionado = this.normalizarAno(this.anoSelecionado);
        this.mesAplicado = this.mesSelecionado;
        this.anoAplicado = this.anoSelecionado;
        this.ufAplicada = this.ufSelecionada;
        this.aplicarChange$.next();

        this.situacaoItens = [];
        this.sexoItens = [];
        this.movimentoItens = [];
        this.cancelamentosItens = [];
        this.enderecoTipoItens = [];
        this.situacaoLinhas = [];
        this.sexoLinhas = [];
        this.movimentoLinhas = [];
        this.cancelamentosLinhas = [];

        const uf = this.ufSelecionada ?? undefined;
        const mes = this.mesSelecionado;
        const ano = this.anoSelecionado;

        this.pendentes = 0;
        this.carregando = true;

        this.carregar(this.feature.consultarSituacaoMensalSituacao(uf), (dados) => {
            this.situacaoItens = this.ordenar(dados);
            this.situacaoLinhas = this.decorar(this.situacaoItens);
        });
        this.carregar(this.feature.consultarSituacaoMensalSexo(uf), (dados) => {
            this.sexoItens = this.ordenar(dados);
            this.sexoLinhas = this.decorar(this.sexoItens);
        });
        this.carregar(this.feature.consultarSituacaoMensalMovimento(uf, mes, ano), (dados) => {
            this.movimentoItens = this.ordenar(dados);
            this.movimentoLinhas = this.decorar(this.movimentoItens);
        });
        this.carregar(this.feature.consultarSituacaoMensalCancelamentos(uf, mes, ano), (dados) => {
            this.cancelamentosItens = this.ordenar(dados);
            this.cancelamentosLinhas = this.decorar(this.cancelamentosItens);
        });
        this.carregar(this.feature.consultarSituacaoMensalEnderecoTipo(uf), (dados) => (this.enderecoTipoItens = dados ?? []));
    }

    obterDescricaoItem(item: MedicoEstatisticaAgrupadaResponse): string {
        return item.descricao?.trim() || item.codigo?.trim() || 'Não informado';
    }

    // --- Visualização analítica (drill-down) ---

    verDetalhe(card: DrillCard, linha: LinhaBloco): void {
        if (!linha.codigo || (linha.total ?? 0) <= 0) {
            return;
        }
        this.detalheChange$.next();
        this.detalheAtivo = true;
        this.detalheCard = card;
        this.detalheCodigo = linha.codigo;
        this.detalheRotulo = linha.descricao;
        this.firstDetalhe = 0;
        this.linhasDetalhe = [];
        this.totalDetalhe = 0;
        this.carregarDetalhe();
    }

    voltarParaCards(): void {
        this.detalheChange$.next();
        this.detalheAtivo = false;
        this.detalheCard = null;
        this.detalheCodigo = null;
        this.linhasDetalhe = [];
        this.totalDetalhe = 0;
        this.firstDetalhe = 0;
        this.carregandoDetalhe = false;
    }

    onPageChangeDetalhe(event: PaginatorState): void {
        this.detalheChange$.next();
        this.firstDetalhe = event.first ?? 0;
        this.rowsDetalhe = event.rows ?? this.rowsDetalhe;
        this.carregarDetalhe();
    }

    obterIndicadorDetalhe(): IndicadorPendencia | null {
        if (!this.detalheAtivo || !this.detalheCard) {
            return null;
        }
        const titulo = this.detalheCard === 'situacao' ? 'Situação atual' : `Movimento · ${this.periodoCurto}`;
        return {
            id: `situacao-mensal-${this.detalheCard}`,
            titulo: `${titulo} — ${this.detalheRotulo}`,
            descricao: `${this.ufAplicadaLabel} · ${this.periodoLabel} · ${this.detalheRotulo}.`,
            grupo: 'Situacao' as IndicadorPendencia['grupo'],
            classeGrupo: 'grupo-situacao',
            quantidade: this.totalDetalhe || null
        };
    }

    private carregarDetalhe(): void {
        if (!this.detalheCard || !this.detalheCodigo) {
            return;
        }
        const card = this.detalheCard;
        const codigo = this.detalheCodigo;
        const uf = this.ufAplicada ?? undefined;
        const size = this.rowsDetalhe;
        const page = size > 0 ? Math.floor(this.firstDetalhe / size) : 0;

        this.carregandoDetalhe = true;
        this.feature
            .consultarSituacaoMensalMedicos(card, uf, this.mesAplicado, this.anoAplicado, page, size, codigo)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                takeUntil(this.detalheChange$),
                // markForCheck: estado gravado em callback assíncrono sob o layout OnPush;
                // sem marcar dirty a view só atualizaria após um clique.
                finalize(() => {
                    if (this.detalheCard === card && this.detalheCodigo === codigo) {
                        this.carregandoDetalhe = false;
                    }
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (resp) => {
                    if (this.detalheCard !== card || this.detalheCodigo !== codigo) {
                        return;
                    }
                    this.linhasDetalhe = (resp?.content ?? []).map((item) => this.mapLinha(item));
                    this.totalDetalhe = resp?.totalElements ?? 0;
                },
                error: () => {
                    if (this.detalheCard !== card || this.detalheCodigo !== codigo) {
                        return;
                    }
                    this.linhasDetalhe = [];
                    this.totalDetalhe = 0;
                }
            });
    }

    private mapLinha(item: MedicoPendenciaResponse): PendenciaLinha {
        return {
            uf: item.ufSgUf ?? '',
            crm: item.nuCrm ?? '',
            nome: item.nmSolicitante ?? '',
            situacao: item.dsSituacao ?? item.inSituacao ?? '',
            codigoSituacao: item.inSituacao ?? '',
            detalhe: item.informacaoComplementar ?? ''
        };
    }

    // --- KPIs (derivados dos dados já carregados, sem chamada extra) ---

    get totalInscritos(): number {
        return this.situacaoItens.reduce((acc, i) => acc + (i.total ?? 0), 0);
    }

    get totalAtivos(): number {
        return this.situacaoItens.find((i) => (i.codigo ?? '').trim().toUpperCase() === 'A')?.total ?? 0;
    }

    get percentualAtivos(): number {
        const base = this.totalInscritos;
        return base ? (this.totalAtivos / base) * 100 : 0;
    }

    get novasInscricoes(): number {
        return this.movimentoItens.filter((i) => (i.codigo ?? '').trim().toUpperCase().startsWith('INSCRICAO')).reduce((acc, i) => acc + (i.total ?? 0), 0);
    }

    get baixasMes(): number {
        return this.cancelamentosItens.reduce((acc, i) => acc + (i.total ?? 0), 0);
    }

    get saldoMes(): number {
        return this.novasInscricoes - this.baixasMes;
    }

    get saldoMesAbs(): number {
        return Math.abs(this.saldoMes);
    }

    get periodoLabel(): string {
        const mes = this.meses.find((m) => m.value === this.mesAplicado)?.label ?? '';
        return mes ? `${mes} de ${this.anoAplicado}` : String(this.anoAplicado);
    }

    /**
     * Mês e ano só filtram movimento e cancelamentos. Situação, sexo e endereço vêm sempre
     * na posição atual, então o cabeçalho precisa dizer a que o período se aplica.
     */
    get resumoSubtitulo(): string {
        return `Movimento de ${this.periodoLabel} · demais indicadores na posição atual`;
    }

    get periodoCurto(): string {
        const mes = this.meses.find((m) => m.value === this.mesAplicado)?.label ?? '';
        return mes ? `${mes.slice(0, 3)}/${this.anoAplicado}` : String(this.anoAplicado);
    }

    get ufAplicadaLabel(): string {
        return this.ufAplicada ?? 'Brasil';
    }

    get totaisEndereco(): MedicoSituacaoEnderecoTipoResponse {
        return this.enderecoTipoItens.reduce(
            (acc, l) => ({
                faixaEndereco: 'BR',
                descricao: 'Total',
                primaria: (acc.primaria ?? 0) + (l.primaria ?? 0),
                secundaria: (acc.secundaria ?? 0) + (l.secundaria ?? 0),
                provisoria: (acc.provisoria ?? 0) + (l.provisoria ?? 0),
                total: (acc.total ?? 0) + (l.total ?? 0)
            }),
            { faixaEndereco: 'BR', descricao: 'Total', primaria: 0, secundaria: 0, provisoria: 0, total: 0 } as MedicoSituacaoEnderecoTipoResponse
        );
    }

    private decorar(itens: MedicoEstatisticaAgrupadaResponse[]): LinhaBloco[] {
        const totais = itens.map((i) => i.total ?? 0);
        const soma = totais.reduce((a, b) => a + b, 0);
        const maior = totais.reduce((a, b) => Math.max(a, b), 0);
        return itens.map((i) => {
            const total = i.total ?? 0;
            return {
                descricao: this.obterDescricaoItem(i),
                total,
                share: soma ? (total / soma) * 100 : 0,
                barra: maior ? (total / maior) * 100 : 0,
                dominante: maior > 0 && total === maior,
                codigo: i.codigo?.trim() || null
            };
        });
    }

    private carregar<T>(fonte: Observable<T[]>, aplicar: (dados: T[]) => void): void {
        this.pendentes += 1;
        fonte
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                takeUntil(this.aplicarChange$),
                finalize(() => {
                    this.pendentes -= 1;
                    if (this.pendentes <= 0) {
                        this.carregando = false;
                    }
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (dados) => aplicar(dados ?? []),
                error: () => aplicar([])
            });
    }

    private ordenar(itens: MedicoEstatisticaAgrupadaResponse[] | null | undefined): MedicoEstatisticaAgrupadaResponse[] {
        return (itens ?? []).slice().sort((a, b) => {
            const ordemA = a.ordem ?? Number.MAX_SAFE_INTEGER;
            const ordemB = b.ordem ?? Number.MAX_SAFE_INTEGER;
            if (ordemA !== ordemB) {
                return ordemA - ordemB;
            }
            return (b.total ?? 0) - (a.total ?? 0);
        });
    }

    private configurarUfPorToken(): void {
        const ufToken = this.authService.ufDoUsuario()?.trim().toUpperCase() ?? '';
        const ufTokenValida = /^[A-Z]{2}$/.test(ufToken) ? ufToken : '';

        if (ufTokenValida && ufTokenValida !== 'BR') {
            this.ufTokenRestrita = ufTokenValida;
            this.ufsPermitidas = [ufTokenValida];
            this.ufSelecionada = ufTokenValida;
            this.ufSelecaoGestao.definir(ufTokenValida);
            return;
        }

        this.ufTokenRestrita = null;
        this.ufsPermitidas = null;
        this.ufSelecionada = this.ufSelecaoGestao.uf();
    }

    private normalizarUfSelecionada(uf?: string | null): string | null {
        const ufNormalizada = String(uf ?? '')
            .trim()
            .toUpperCase();

        if (!ufNormalizada || ufNormalizada === 'BR' || !/^[A-Z]{2}$/.test(ufNormalizada)) {
            return this.ufTokenRestrita;
        }

        if (this.ufTokenRestrita && ufNormalizada !== this.ufTokenRestrita) {
            return this.ufTokenRestrita;
        }

        return ufNormalizada;
    }

    private normalizarMes(valor: number): number {
        const mes = Math.trunc(Number(valor));
        if (!Number.isFinite(mes) || mes < 1 || mes > 12) {
            return new Date().getMonth() + 1;
        }
        return mes;
    }

    private normalizarAno(valor: number): number {
        const ano = Number(valor);
        if (!Number.isFinite(ano)) {
            return this.anoAtual;
        }
        return Math.min(Math.max(Math.trunc(ano), 1900), this.anoAtual + 1);
    }
}
